import { isAxiosError } from 'axios'
import { message } from 'ant-design-vue'

import { resolveAuthTokenRaw } from '@/stores/auth-utils'

import { notifyHttpSessionExpired } from './session'
import { HttpError } from './types'

const STATUS_MESSAGE_MAP: Record<number, string> = {
  400: '请求参数有误，请检查后重试',
  403: '您没有权限执行此操作',
  404: '请求的资源不存在',
  500: '服务器内部错误，请稍后重试',
  502: '网关错误，请稍后重试',
  503: '服务暂不可用，请稍后重试',
}

export interface HttpResponseErrorHandlerOptions {
  showToast?: boolean
  respectSkipGlobalErrorHandler?: boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

export function extractHttpErrorMessage(data: unknown): string | undefined {
  if (!isRecord(data)) return undefined
  const errors = data.errors
  if (Array.isArray(errors) && isRecord(errors[0])) {
    const m = errors[0].message
    if (typeof m === 'string' && m.trim()) return m
  }
  if (typeof data.message === 'string' && data.message.trim()) return data.message
  if (typeof data.msg === 'string' && data.msg.trim()) return data.msg
  return undefined
}

function isExpiredSessionResponse(data: unknown): boolean {
  if (!isRecord(data) || !Array.isArray(data.errors)) return false
  return data.errors.some((error) => isRecord(error) && error.code === 'EXPIRED_SESSION')
}

export function toHttpError(error: unknown): HttpError {
  if (isAxiosError(error)) {
    const status = error.response?.status
    const data = error.response?.data
    const msg =
      extractHttpErrorMessage(data) ||
      error.message ||
      (status != null ? `请求失败（${status}）` : '网络请求失败')
    return new HttpError(msg, {
      status,
      code: error.code,
      body: data,
    })
  }
  if (error instanceof Error) {
    return new HttpError(error.message, { code: error.name })
  }
  return new HttpError('未知错误')
}

function shouldToast(error: unknown, options: HttpResponseErrorHandlerOptions): boolean {
  if (!options.showToast) return false
  if (!options.respectSkipGlobalErrorHandler) return true
  const config = isRecord(error) && isRecord(error.config) ? error.config : null
  return config?.skipGlobalErrorHandler !== true
}

export function createHttpResponseErrorHandler(
  options: HttpResponseErrorHandlerOptions = {},
): (error: unknown) => Promise<never> {
  return (error: unknown) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      const hadToken = !!resolveAuthTokenRaw()
      const data = error.response.data
      const isExpiredSession = isExpiredSessionResponse(data)

      // 临时 token（temp: true）触发 EXPIRED_SESSION，不清会话
      if (hadToken && !isExpiredSession) {
        notifyHttpSessionExpired()
      }

      const msg =
        extractHttpErrorMessage(data) ??
        (hadToken ? '登录已过期，请重新登录' : '账号或密码不正确')
      return Promise.reject(new HttpError(msg, {
        status: 401,
        code: isExpiredSession ? 'EXPIRED_SESSION' : error.code,
        body: data,
      }))
    }

    if (isAxiosError(error) && error.code === 'ECONNABORTED') {
      const msg = '请求超时，请检查网络后重试'
      if (shouldToast(error, options)) message.error(msg)
      return Promise.reject(new HttpError(msg, { code: 'ECONNABORTED' }))
    }

    if (isAxiosError(error) && error.response) {
      const status = error.response.status
      const data = error.response.data
      const msg = extractHttpErrorMessage(data) ?? STATUS_MESSAGE_MAP[status] ?? `请求失败（${status}）`
      if (shouldToast(error, options)) message.error(msg)
      return Promise.reject(new HttpError(msg, {
        status,
        code: error.code,
        body: data,
      }))
    }

    if (isAxiosError(error)) {
      const msg = '网络异常，请检查网络连接'
      if (shouldToast(error, options)) message.error(msg)
      return Promise.reject(new HttpError(msg, { code: error.code }))
    }

    const httpError = toHttpError(error)
    if (shouldToast(error, options)) message.error(httpError.message || '网络异常，请检查网络连接')
    return Promise.reject(httpError)
  }
}
